import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { openaiService } from '../../services/openaiService';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Checkbox } from '../../components/ui/Checkbox';
import Select from '../../components/ui/Select';

const AIWorkoutRecommendations = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [goals, setGoals] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('beginner');
  const [equipment, setEquipment] = useState([]);
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const goalOptions = [
    'Build Muscle',
    'Lose Weight',
    'Increase Strength',
    'Improve Endurance',
    'Enhance Flexibility',
    'General Fitness'
  ];

  const equipmentOptions = [
    'Dumbbells',
    'Barbell',
    'Bench',
    'Pull-up Bar',
    'Resistance Bands',
    'Kettlebells',
    'Cable Machine',
    'Treadmill',
    'Stationary Bike',
    'No Equipment'
  ];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadSavedPlans();
    }
  }, [user, currentTenant]);

  const loadSavedPlans = async () => {
    try {
      const { data, error: fetchError } = await supabase
        ?.from('ai_workout_recommendations')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (fetchError) throw fetchError;
      setSavedPlans(data || []);
    } catch (err) {
      console.error('Error loading saved plans:', err);
    }
  };

  const handleGoalToggle = (goal) => {
    setGoals(prev => 
      prev?.includes(goal) 
        ? prev?.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const handleEquipmentToggle = (item) => {
    setEquipment(prev => 
      prev?.includes(item) 
        ? prev?.filter(e => e !== item)
        : [...prev, item]
    );
  };

  const handleGeneratePlan = async () => {
    if (goals?.length === 0) {
      setError('Please select at least one fitness goal');
      return;
    }

    if (equipment?.length === 0) {
      setError('Please select available equipment');
      return;
    }

    setLoading(true);
    setError('');
    setWorkoutPlan(null);
    setSelectedPlanId(null);

    try {
      const result = await openaiService?.getWorkoutSuggestions(
        goals,
        experienceLevel,
        equipment
      );

      if (result?.success) {
        setWorkoutPlan(result?.response);

        const { data, error: saveError } = await supabase
          ?.from('ai_workout_recommendations')
          ?.insert({
            tenant_id: currentTenant?.id,
            user_id: user?.id,
            goals,
            experience_level: experienceLevel,
            available_equipment: equipment,
            workout_plan: result?.response,
            ai_response_data: {
              model: 'gpt-4o-mini',
              tokens_used: result?.usage?.total_tokens,
              session_type: 'workout_plan'
            }
          })
          ?.select()
          ?.single();

        if (saveError) throw saveError;
        if (data) {
          setSelectedPlanId(data?.id);
        }
        await loadSavedPlans();
      } else {
        setError(result?.error || 'Failed to generate workout plan');
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate workout plan');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSavedPlan = (plan) => {
    setWorkoutPlan(plan?.workout_plan);
    setGoals(plan?.goals || []);
    setExperienceLevel(plan?.experience_level || 'beginner');
    setEquipment(plan?.available_equipment || []);
    setSelectedPlanId(plan?.id);
    setError('');
  };

  const handleStartWorkout = () => {
    if (selectedPlanId) {
      navigate('/active-workout', { state: { planId: selectedPlanId, workoutPlan, workoutName: `${goals?.join(', ')} - ${experienceLevel}` } });
    } else {
      setError('Please generate or select a workout plan first');
    }
  };

  const handleViewAnalytics = () => {
    navigate('/workout-analytics-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-6xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Workout Recommendations</h1>
          <p className="text-muted-foreground">Get personalized workout plans powered by AI based on your goals and available equipment</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-foreground mb-4">Your Preferences</h2>

              {/* Fitness Goals */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Fitness Goals *
                </label>
                <div className="space-y-2">
                  {goalOptions?.map(goal => (
                    <div key={goal} className="flex items-center gap-2">
                      <Checkbox
                        checked={goals?.includes(goal)}
                        onChange={() => handleGoalToggle(goal)}
                        id={`goal-${goal}`}
                      />
                      <label htmlFor={`goal-${goal}`} className="text-sm text-foreground cursor-pointer">
                        {goal}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Experience Level */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Experience Level
                </label>
                <Select
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e?.target?.value)}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </Select>
              </div>

              {/* Available Equipment */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Available Equipment *
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {equipmentOptions?.map(item => (
                    <div key={item} className="flex items-center gap-2">
                      <Checkbox
                        checked={equipment?.includes(item)}
                        onChange={() => handleEquipmentToggle(item)}
                        id={`equipment-${item}`}
                      />
                      <label htmlFor={`equipment-${item}`} className="text-sm text-foreground cursor-pointer">
                        {item}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
                  <Icon name="AlertCircle" className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <Button
                onClick={handleGeneratePlan}
                disabled={loading || goals?.length === 0 || equipment?.length === 0}
                iconName={loading ? 'Loader2' : 'Sparkles'}
                iconPosition="left"
                iconClassName={loading ? 'animate-spin' : ''}
                className="w-full"
              >
                {loading ? 'Generating...' : 'Generate Workout Plan'}
              </Button>

              {selectedPlanId && (
                <Button
                  onClick={handleStartWorkout}
                  variant="default"
                  className="w-full mt-3"
                  iconName="Play"
                  iconPosition="left"
                >
                  Start This Workout
                </Button>
              )}

              <Button
                onClick={handleViewAnalytics}
                variant="outline"
                className="w-full mt-3"
                iconName="BarChart3"
                iconPosition="left"
              >
                View Analytics
              </Button>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {workoutPlan ? (
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Dumbbell" className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Your Personalized Workout Plan</h2>
                    <p className="text-sm text-muted-foreground">
                      {experienceLevel?.charAt(0)?.toUpperCase() + experienceLevel?.slice(1)} level • {goals?.join(', ')}
                    </p>
                  </div>
                </div>

                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-foreground leading-relaxed">
                    {workoutPlan}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Sparkles" className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Workout Plan Yet</h3>
                <p className="text-muted-foreground mb-6">
                  Select your goals and available equipment, then click "Generate Workout Plan" to get started
                </p>
              </div>
            )}

            {/* Saved Plans */}
            {savedPlans?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Previously Generated Plans</h3>
                <div className="space-y-3">
                  {savedPlans?.map(plan => (
                    <div
                      key={plan?.id}
                      className="bg-card rounded-lg border border-border p-4 hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => handleViewSavedPlan(plan)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Target" className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium text-foreground">
                              {plan?.goals?.join(', ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>{plan?.experience_level}</span>
                            <span>•</span>
                            <span>{plan?.available_equipment?.length} equipment items</span>
                            <span>•</span>
                            <span>{new Date(plan?.created_at)?.toLocaleDateString()}</span>
                          </div>
                        </div>
                        <Icon name="ChevronRight" className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default AIWorkoutRecommendations;